import ClearIcon from "@mui/icons-material/Clear";
import {
	Box,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Radio,
	Select,
	type SelectChangeEvent,
	type SxProps,
	type Theme,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { DatePickerProps } from "@mui/x-date-pickers/DatePicker";
import { DateTime } from "luxon";
import { type CSSProperties, type FC, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { INK, NEUTRAL } from "@/theme/tokens";
import { DateInput } from "../Inputs/DateInput";

const QUARTER_LOOKBACK = 12; // current quarter + 12 prior = ~3 years of options

type QuarterOption = { value: string; year: number; quarter: number };

type Props = {
	disabled?: boolean;
	onEnabledChange?: (enabled: boolean) => void;
	startDate?: DateTime;
	endDate?: DateTime;
	onStartDateChange: (date: DateTime) => void;
	onEndDateChange: (date: DateTime) => void;
	minDate?: DateTime;
	/** Overrides the end picker's minimum date (defaults to the start date). Used to enforce a minimum range span. */
	endMinDate?: DateTime;
	/** Overrides the end picker's maximum date (defaults to `maxDate`). Used to enforce a maximum range span. */
	endMaxDate?: DateTime;
	maxDate?: DateTime;
	/**
	 * By default the start picker is capped at the current end date (start ≤ end). Set this to let
	 * the start be picked freely (up to `maxDate`); callers are then responsible for realigning the
	 * end when the start moves past it.
	 */
	allowStartAfterEnd?: boolean;
	includeLeadingToggle?: boolean;
	/**
	 * Dense variant that fits BOTH pickers on one row inside a narrow column (a filter rail).
	 *
	 * At the standard size a `type="date"` field demands ~167px of intrinsic width, so the pair
	 * overflows any column under ~340px and wraps — which used to strand the "to" separator at the end
	 * of the first line, pointing at nothing. Compact shrinks each field (see `DateInput`'s `compact`)
	 * and lets the two share the row, which is what a range wants to look like anyway. The "to" is
	 * dropped here: the pickers' own labels already say which end is which, and the word costs width
	 * the fields need.
	 */
	compact?: boolean;
	removeBackground?: boolean;
	removeBorder?: boolean;
	containerSX?: SxProps<Theme>;
	dateInputSX?: SxProps<Theme>;
	padding?: CSSProperties["padding"];
	label?: string;
	onClear?: () => void;
	/** When true, render a year-quarter dropdown (`2026 - Q2`) above the manual pickers. */
	showQuarterSelect?: boolean;
	shouldDisabledStartDate?: DatePickerProps["shouldDisableDate"];
	shouldDisabledEndDate?: DatePickerProps["shouldDisableDate"];
	/** Test ids for the individual pickers, for callers that need to target one end of the range. */
	startDateTestId?: string;
	endDateTestId?: string;
	/** Already-translated overrides for the picker labels, e.g. to qualify the expected weekday. */
	startLabel?: string;
	endLabel?: string;
};

const quarterRange = (year: number, quarter: number): { start: DateTime; end: DateTime } => {
	const start = DateTime.fromObject({ year, month: (quarter - 1) * 3 + 1, day: 1 });
	return { start, end: start.endOf("quarter").startOf("day") };
};

const buildQuarterOptions = (lookback: number = QUARTER_LOOKBACK): QuarterOption[] => {
	const now = DateTime.now();
	const options: QuarterOption[] = [];
	for (let i = 0; i <= lookback; i++) {
		const dt = now.minus({ quarters: i });
		options.push({ value: `${dt.year}-Q${dt.quarter}`, year: dt.year, quarter: dt.quarter });
	}
	return options;
};

export const DateRangeSelector: FC<Props> = ({
	disabled = false,
	onEnabledChange,
	startDate,
	endDate,
	onStartDateChange,
	onEndDateChange,
	minDate,
	endMinDate,
	endMaxDate,
	maxDate,
	allowStartAfterEnd = false,
	includeLeadingToggle = false,
	// DEFAULT: the standard-size row this component shipped with, so every existing caller is unchanged.
	compact = false,
	removeBackground = false,
	removeBorder = false,
	containerSX,
	dateInputSX,
	padding = "12px",
	label,
	onClear,
	showQuarterSelect = false,
	shouldDisabledStartDate,
	shouldDisabledEndDate,
	startDateTestId,
	endDateTestId,
	startLabel,
	endLabel,
}) => {
	const { t } = useTranslation();
	const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
	const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

	const quarterLabelId = useId();
	const quarterOptions = useMemo<QuarterOption[]>(() => buildQuarterOptions(), []);

	// Derive the dropdown's current value from the date range so it stays in sync when
	// the user picks dates manually (and resets to "" if the range no longer aligns).
	const activeQuarterValue = useMemo<string>(() => {
		if (!startDate || !endDate) return "";
		const match = quarterOptions.find(({ year, quarter }) => {
			const range = quarterRange(year, quarter);
			return range.start.toISODate() === startDate.toISODate() && range.end.toISODate() === endDate.toISODate();
		});
		return match?.value ?? "";
	}, [startDate, endDate, quarterOptions]);

	const handleQuarterChange = (event: SelectChangeEvent<string>) => {
		const option = quarterOptions.find((q) => q.value === event.target.value);
		if (!option) return;
		const range = quarterRange(option.year, option.quarter);
		onStartDateChange(range.start);
		onEndDateChange(range.end);
	};

	const handleStartPickerOpenChange = (open: boolean) => {
		setStartDatePickerOpen(open);
		if (open) setEndDatePickerOpen(false);
	};

	const handleEndPickerOpenChange = (open: boolean) => {
		setEndDatePickerOpen(open);
		if (open) setStartDatePickerOpen(false);
	};

	const handleStartDateChange = (date: DateTime) => {
		// Automatically open the end date picker after selecting start date
		onStartDateChange(date);
		setStartDatePickerOpen(false);
		setEndDatePickerOpen(true);
	};

	return (
		<Box
			data-testid="date-range-selector"
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				padding: padding,
				borderRadius: "8px",
				backgroundColor: removeBackground || disabled ? "transparent" : NEUTRAL[50],
				border: removeBorder ? "none" : `1px solid ${NEUTRAL[200]}`,
				...containerSX,
			}}
		>
			{includeLeadingToggle && (
				<FormControlLabel
					control={<Radio checked={!disabled} onChange={(e) => onEnabledChange?.(e.target.checked)} sx={{ padding: "4px" }} />}
					label={
						<Typography
							sx={{
								fontWeight: 500,
								fontSize: "14px",
								color: !disabled ? INK.SECONDARY : alpha(INK.SECONDARY, 0.5),
								whiteSpace: "nowrap",
							}}
						>
							{t("custom.range")}
						</Typography>
					}
					sx={{ marginRight: 1 }}
				/>
			)}
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
					alignItems: "start",
					width: `calc(100% - ${includeLeadingToggle ? "50px" : "0px"})`,
					gap: 2,
				}}
			>
				{label && (
					<Typography
						sx={{
							color: INK.SECONDARY,
						}}
					>
						{label}
					</Typography>
				)}
				{showQuarterSelect && (
					<FormControl size="small" disabled={disabled} sx={{ minWidth: 180 }} data-testid="date-range-quarter-select">
						<InputLabel id={quarterLabelId}>{t("date.range.quarter.label")}</InputLabel>
						<Select
							labelId={quarterLabelId}
							value={activeQuarterValue}
							label={t("date.range.quarter.label")}
							onChange={handleQuarterChange}
							inputProps={{ "data-testid": "date-range-quarter-select-input" }}
						>
							{quarterOptions.map((opt) => (
								<MenuItem key={opt.value} value={opt.value} data-testid={`date-range-quarter-${opt.value}`}>
									{`${opt.year} - Q${opt.quarter}`}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				)}
				<Box
					sx={
						compact
							? // nowrap + flex children: the two fields divide the row instead of one of them
								// wrapping under the other. `minWidth: 0` is what lets them shrink below their
								// intrinsic date width at all.
								{
									display: "flex",
									gap: 0.5,
									alignItems: "center",
									flexWrap: "nowrap",
									// The column wrapper above uses `alignItems: "start"`, so without these this row
									// would size to its own min-content (both fields at their intrinsic date width) and
									// overflow the column instead of dividing it.
									width: "100%",
									minWidth: 0,
									"& > .MuiFormControl-root": { flex: "1 1 0", minWidth: 0 },
								}
							: { display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }
					}
				>
					<DateInput
						compact={compact}
						disabled={disabled}
						value={startDate}
						onChange={handleStartDateChange}
						label={startLabel ?? t("start.date")}
						minDate={minDate}
						maxDate={allowStartAfterEnd ? maxDate : (endDate ?? maxDate)}
						open={startDatePickerOpen}
						onOpenChange={handleStartPickerOpenChange}
						formControlSx={dateInputSX}
						datePickerToolbarTitle={t("select.start.date")}
						shouldDisableDate={shouldDisabledStartDate}
						dataTestId={startDateTestId}
					/>
					{/* Dropped when compact — see the prop's doc: the labels disambiguate the two ends, and the
					word costs width the fields need to fit the row at all. */}
					{!compact && <Typography sx={{ color: alpha(INK.SECONDARY, 0.5), fontSize: "14px" }}>{t("to")}</Typography>}
					<DateInput
						compact={compact}
						disabled={disabled}
						value={endDate}
						onChange={onEndDateChange}
						label={endLabel ?? t("end.date")}
						minDate={endMinDate ?? startDate ?? minDate}
						maxDate={endMaxDate ?? maxDate}
						open={endDatePickerOpen}
						onOpenChange={handleEndPickerOpenChange}
						formControlSx={dateInputSX}
						datePickerToolbarTitle={t("select.end.date")}
						shouldDisableDate={shouldDisabledEndDate}
						dataTestId={endDateTestId}
					/>
					{!!onClear && (
						<IconButton onClick={onClear}>
							<ClearIcon />
						</IconButton>
					)}
				</Box>
			</Box>
		</Box>
	);
};
